import React from 'react';
import { motion } from 'framer-motion';
import { CheckIcon } from 'lucide-react';
import { InteriorSlide } from './SlideShell';
import type { SlideProps } from './SlideShell';
import { jascaydCampaignContent, jascaydCampaignManagement, jascaydPilotMonths, jascaydPilotTotal } from '../../data/jascaydOffer';
interface CostCardProps {
  title: string;
  billing: string;
  price: string;
  priceNote: string;
  includes: string[];
  dark?: boolean;
  delay: number;
}
export function SlideJascaydInvestment({}: SlideProps) {
  const managementTotal = jascaydCampaignManagement.monthly * jascaydPilotMonths;
  return <InteriorSlide data-id="element-296" data-mp="1a1oh5477eay3">
      <motion.header initial={{
      opacity: 0,
      y: 18
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.5
    }} className="flex-shrink-0" data-id="element-297" data-mp="1a27t84a6a547">
        <p className="mb-2 text-[clamp(0.7rem,1vw,0.9rem)] font-bold uppercase tracking-[0.2em] text-[var(--brand)]" data-id="element-298" data-mp="hj13g9ujdy71">
          Indicative investment · {jascaydPilotMonths}-month pilot
        </p>
        <h2 className="max-w-5xl text-[clamp(1.9rem,3.6vw,3.3rem)] font-extrabold leading-[1.02] tracking-[-0.04em] text-[var(--fg)]" data-id="element-299" data-mp="5x74inlgpz77">
          Create the content once.{' '}
          <span className="text-[var(--brand)]" data-id="element-300" data-mp="fnjremgr24gi">Learn every month.</span>
        </h2>
      </motion.header>

      <div className="mt-[clamp(1rem,2vw,1.75rem)] grid min-h-0 flex-1 grid-cols-1 gap-[clamp(0.75rem,1.5vw,1.35rem)] md:grid-cols-2" data-id="element-301" data-mp="jpwvl6fulub6">
        <CostCard title={jascaydCampaignContent.title} billing={jascaydCampaignContent.billing} price={formatEur(jascaydCampaignContent.price)} priceNote="one-off" includes={jascaydCampaignContent.includes} delay={0.15} data-id="element-302" data-mp="3j6mqoanjieak" />
        <CostCard title={jascaydCampaignManagement.title} billing={jascaydCampaignManagement.billing} price={formatEur(jascaydCampaignManagement.monthly)} priceNote={`per month · ${formatEur(managementTotal)} over ${jascaydPilotMonths} months`} includes={jascaydCampaignManagement.includes} dark delay={0.28} data-id="element-303" data-mp="10qko5laq6zx" />
      </div>

      <motion.div initial={{
      opacity: 0,
      y: 16
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.45,
      duration: 0.5
    }} className="mt-[clamp(0.75rem,1.5vw,1.25rem)] flex flex-shrink-0 flex-wrap items-center justify-between gap-4 rounded-2xl bg-[var(--brand)] px-[clamp(1rem,2vw,1.85rem)] py-[clamp(0.8rem,1.4vw,1.2rem)] text-white" data-id="element-304" data-mp="34fc4hysitc4m">
        <div data-id="element-305" data-mp="2ksxq8j3tzxzp">
          <p className="text-[clamp(0.7rem,0.95vw,0.9rem)] font-bold uppercase tracking-[0.15em]" data-id="element-306" data-mp="2kt7jcfk6al9m">
            Total {jascaydPilotMonths}-month pilot
          </p>
          <p className="text-[clamp(0.72rem,0.95vw,0.9rem)] text-[var(--brand-lighter)]" data-id="element-307" data-mp="2upk76wt1e4wg">
            {formatEur(jascaydCampaignContent.price)} content +{' '}
            {formatEur(managementTotal)} campaign management
          </p>
        </div>
        <span className="text-[clamp(1.6rem,3vw,2.6rem)] font-extrabold tabular-nums tracking-[-0.03em]" data-id="element-308" data-mp="2214wgmgxu8qx">
          {formatEur(jascaydPilotTotal)}
        </span>
      </motion.div>

      <p className="mt-[clamp(0.5rem,1vw,0.8rem)] flex-shrink-0 text-[clamp(0.7rem,0.9vw,0.85rem)] text-[var(--muted)]" data-id="element-309" data-mp="mauib0qde1so">
        Indicative pricing in EUR, excl. VAT and media. Per-market localisation
        and translation quoted once priority markets are confirmed.
      </p>
    </InteriorSlide>;
}
function CostCard({
  title,
  billing,
  price,
  priceNote,
  includes,
  dark = false,
  delay
}: CostCardProps) {
  return <motion.article initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    delay,
    duration: 0.5
  }} className={`flex min-h-0 flex-col rounded-2xl p-[clamp(1rem,2vw,1.85rem)] ${dark ? 'bg-[var(--fg)] text-white' : 'bg-[var(--surface)] text-[var(--fg)]'}`} data-id="element-310" data-mp="156knewtlneas">
      <p className={`text-[clamp(0.62rem,0.85vw,0.78rem)] font-bold uppercase tracking-[0.2em] ${dark ? 'text-[var(--brand-lighter)]' : 'text-[var(--brand)]'}`} data-id="element-311" data-mp="3gy7umbg2d978">
        {billing}
      </p>
      <h3 className="mt-1 text-[clamp(1.2rem,1.9vw,1.75rem)] font-extrabold tracking-[-0.03em]" data-id="element-312" data-mp="3rgd0g41p39mi">
        {title}
      </h3>
      <div className="mt-[clamp(0.5rem,1vw,0.85rem)] flex flex-wrap items-baseline gap-x-2" data-id="element-313" data-mp="3miumu0cqg686">
        <span className="text-[clamp(2rem,3.8vw,3.4rem)] font-extrabold tabular-nums leading-none tracking-[-0.04em]" data-id="element-314" data-mp="3iiasf4jjngpq">
          {price}
        </span>
        <span className={`text-[clamp(0.75rem,1vw,0.95rem)] font-semibold ${dark ? 'text-white/60' : 'text-[var(--muted)]'}`} data-id="element-315" data-mp="e2rgpz19qply">
          {priceNote}
        </span>
      </div>
      <ul className={`mt-auto flex flex-col gap-[clamp(0.35rem,0.7vw,0.6rem)] border-t pt-[clamp(0.6rem,1.2vw,1rem)] ${dark ? 'border-white/10' : 'border-[var(--fg)]/10'}`} data-id="element-316" data-mp="52mlen9cnho5">
        {includes.map(item => <li key={item} className="flex items-start gap-2 text-[clamp(0.8rem,1.1vw,1.05rem)] font-semibold" data-id="element-317" data-mp="2pqr63brnhmji">
            <CheckIcon className={`mt-0.5 h-4 w-4 flex-shrink-0 ${dark ? 'text-[var(--brand-lighter)]' : 'text-[var(--brand)]'}`} aria-hidden="true" data-id="element-318" data-mp="1a8l47bew0v93" />
            {item}
          </li>)}
      </ul>
    </motion.article>;
}
function formatEur(value: number) {
  return `€${value.toLocaleString('en-US')}`;
}